import { useInstanceQuery } from '../../../graphql/containers/query';
import { AssignmentType, IProduct, ISalesOrderLine, ISupplier, Maybe } from '../../../graphql/schema/p4types';
import ProductQuery from '../../../graphql/queries/product/ProductQuery.graphql';
import { Supplier } from '../../../graphql/entities';
import React, { useCallback, useState } from 'react';
import Dropdown from '../../connectedForm/Dropdown';
import { BluePill, Description, DescriptionPill, Option } from '../../DropdownEditor/CustomOptions';
import BlurTextField from '../../connectedForm/BlurTextField';
import { InfoButton, SecondaryButton, WarningButton } from '../../connectedForm/Button';
import { ButtonContainerDiv } from '../ButtonContainerDiv';
import { ModalBottomButtonContainer } from '../Styles';
import { BasicHeader } from '../../../actions/BasicHeader';
import QueryDropdown from '../../connectedForm/QueryDropdown';
import { IDropdownOptionRenderer } from '../../../@types/entityTypes';
import { Text } from '@fluentui/react-components';
import FluentModal from '../FluentModal';

export const SupplierOption: IDropdownOptionRenderer = ({
  option: { value, flag, supplier: { name, reference } = {} },
}: {
  option: {
    value: any;
    flag: string;
    supplier: { name?: string; reference?: string };
  };
}) => {
  const referencePill = reference ? <DescriptionPill>{reference}</DescriptionPill> : null;
  const flagPill = flag ? <BluePill>{flag}</BluePill> : null;
  return (
    <Option>
      <b>{name || value}</b> {referencePill}
      <Description>{flagPill}</Description>
    </Option>
  );
};

enum SupplierChoice {
  Main = 'MAIN',
  Other = 'OTHER',
  Later = 'LATER',
}

type IShortageSupplierOption = {
  flag?: SupplierChoice;
  key: SupplierChoice | number;
  value: string;
  supplier?: Maybe<ISupplier>;
  leadTimeDays?: number;
};

export const useSupplierOptions = ({ product }: { product: IProduct | null }) => {
  const supplierOptions: Array<IShortageSupplierOption> = [];
  const mainSupplier = product?.mainSupplierDetails?.supplier;
  if (mainSupplier) {
    supplierOptions.push({
      flag: SupplierChoice.Main,
      key: mainSupplier.id,
      value: 'Main Supplier',
      supplier: mainSupplier,
      leadTimeDays: product?.mainSupplierDetails?.leadTimeDays,
    });
  }
  product?.alternateSupplierDetails?.forEach((supplierDetails) => {
    if (supplierDetails && supplierDetails?.supplier?.id !== mainSupplier?.id) {
      supplierOptions.push({
        key: supplierDetails.id,
        value: 'Alternative Supplier',
        supplier: supplierDetails.supplier,
        leadTimeDays: supplierDetails.leadTimeDays,
      });
    }
  });
  supplierOptions.push({ key: SupplierChoice.Other, value: 'Other Supplier' });
  supplierOptions.push({ key: SupplierChoice.Later, value: 'Choose Later' });

  return supplierOptions;
};

export const RaiseShortageSection = ({
  value,
  onClose,
  onRaiseShortage,
  onReviewLater,
}: {
  value: ISalesOrderLine;
  onClose: () => void;
  onRaiseShortage: (supplier: Maybe<ISupplier>) => void;
  onReviewLater: () => void;
}) => {
  const product = useInstanceQuery<IProduct>(ProductQuery, value?.product?.id);
  const [supplierState, setSupplierState] = useState<{
    supplier: Maybe<ISupplier>;
    leadTime: Maybe<number>;
    choice: number | Maybe<SupplierChoice>;
  }>({ supplier: null, leadTime: null, choice: null });

  const handleRaiseShortage = useCallback(() => {
    onRaiseShortage(supplierState.supplier);
  }, [onRaiseShortage, supplierState.supplier]);

  const supplierOptions = useSupplierOptions({ product });

  const existingShortage = value.assignments?.filter(
    (assignment) => assignment?.assignmentType === AssignmentType.Shortage
  )?.[0];
  const hasExistingShortage: boolean = !!existingShortage;

  return (
    <FluentModal isOpen={true} styles={{ main: { minWidth: '800px', paddingBottom: '60px' } }}>
      <BasicHeader>Raise Shortage</BasicHeader>
      <div style={{ padding: '1em' }}>
        {hasExistingShortage ? (
          <Text>Add to existing shortage</Text>
        ) : (
          <Dropdown
            options={supplierOptions}
            optionRenderer={SupplierOption}
            label="Supplier"
            valueKey="key"
            labelKey="value"
            onChange={(value: IShortageSupplierOption) => {
              setSupplierState({
                supplier: value.supplier || null,
                leadTime: value.leadTimeDays || null,
                choice: value.key,
              });
            }}
            value={supplierState.choice}
          />
        )}
        {supplierState.choice === 'OTHER' ? (
          <QueryDropdown
            {...Supplier.dropdown}
            label="Other Supplier"
            onChange={(value: ISupplier) => {
              setSupplierState({
                supplier: value,
                leadTime: value?.defaultLeadTimeDays || null,
                choice: supplierState.choice,
              });
            }}
            value={supplierState.supplier}
            preLoad
            data-testid={'raise-shortage-section-other-supplier'}
          />
        ) : null}
        {supplierState.leadTime ? (
          <BlurTextField
            label="Lead Time"
            value={supplierState.leadTime || '0'}
            readOnly={true}
            data-testid={'raise-shortage-section-lead-time'}
          />
        ) : null}
        <ButtonContainerDiv>
          <SecondaryButton inline={true} onClick={onClose}>
            Cancel
          </SecondaryButton>
          <InfoButton inline={true} onClick={handleRaiseShortage}>
            Raise shortage
          </InfoButton>
          <InfoButton inline={true} onClick={onReviewLater}>
            Review All Shortages At End
          </InfoButton>
        </ButtonContainerDiv>
        <ModalBottomButtonContainer>
          <WarningButton inline={true} onClick={onClose}>
            Continue
          </WarningButton>
        </ModalBottomButtonContainer>
      </div>
    </FluentModal>
  );
};
